import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Link,
  Paper,
} from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
} from "@mui/icons-material";
import { Stream, MediaJob } from "../api/types";

interface StreamsListProps {
  streams: Stream[];
  mediaJobs?: MediaJob[];
}

/**
 * Determines the stream type based on stream properties
 * - If stream has explicit type property, use it
 * - If URL contains .m3u8, it's HLS
 * - Otherwise, it's MP4 (default)
 */
const getStreamType = (stream: Stream): string => {
  if (stream.type) {
    return stream.type === "hls" ? "HLS" : "MP4";
  }
  // Infer from URL
  if (stream.url?.includes(".m3u8")) {
    return "HLS";
  }
  return "MP4";
};

type StreamState = {
  status: "success" | "failure" | "pending";
  label: string;
};

/**
 * Determines the stream state based on media_info and media_jobs
 * - If media_info exists, transcoding succeeded
 * - Otherwise, check the media_job at the same index for state
 */
const getStreamState = (
  stream: Stream,
  index: number,
  mediaJobs?: MediaJob[],
): StreamState => {
  // If media_info is present, it's a success
  if (stream.media_info) {
    return { status: "success", label: "Success" };
  }

  // Check media_job at the same index
  if (mediaJobs && mediaJobs[index]) {
    const jobState = mediaJobs[index].state.toLowerCase();

    if (jobState === "failure" || jobState === "failed") {
      return { status: "failure", label: mediaJobs[index].state };
    }

    // Any other state (processing, pending, queued, etc.) shows in yellow
    return { status: "pending", label: mediaJobs[index].state };
  }

  // No media_info and no corresponding job - assume failure
  return { status: "failure", label: "Failed" };
};

/**
 * Returns the color for a given stream status
 */
const getStateColor = (status: StreamState["status"]): string => {
  switch (status) {
    case "success":
      return "#4caf50"; // Green
    case "failure":
      return "#f44336"; // Red
    case "pending":
      return "#f9a825"; // Yellow/amber
    default:
      return "#9e9e9e"; // Grey
  }
};

/**
 * Returns the icon component for a given stream status
 */
const getStateIcon = (status: StreamState["status"]): React.ReactNode => {
  const color = getStateColor(status);

  switch (status) {
    case "success":
      return <CheckCircle sx={{ fontSize: "16px", color }} />;
    case "failure":
      return <ErrorIcon sx={{ fontSize: "16px", color }} />;
    case "pending":
      return <HourglassEmpty sx={{ fontSize: "16px", color }} />;
    default:
      return null;
  }
};

const StreamsList: React.FC<StreamsListProps> = ({ streams, mediaJobs }) => {
  if (!streams || streams.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No transcoded streams available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="h6"
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          mb: 1,
          color: "#333",
        }}
      >
        Transcoded Streams
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: "8px",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                Type
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                URL
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                State
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {streams.map((stream, index) => {
              const streamType = getStreamType(stream);
              const streamState = getStreamState(stream, index, mediaJobs);

              return (
                <TableRow
                  key={stream.token || stream.id || index}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "#fafafa" },
                  }}
                >
                  <TableCell>
                    <Chip
                      label={streamType}
                      size="small"
                      sx={{
                        backgroundColor:
                          streamType === "HLS" ? "#e3f2fd" : "#f3e5f5",
                        color: streamType === "HLS" ? "#1565c0" : "#7b1fa2",
                        fontWeight: 500,
                        fontSize: "11px",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      href={stream.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontSize: "12px",
                        color: "#1976d2",
                        textDecoration: "none",
                        wordBreak: "break-all",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {stream.url}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {getStateIcon(streamState.status)}
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: getStateColor(streamState.status),
                          textTransform: "capitalize",
                        }}
                      >
                        {streamState.label}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StreamsList;
